// ============================================================
// costFilter.js — parsing and applying a total-cost range filter.
//
// Pure, so the two decisions worth arguing about are testable without a report
// on screen:
//
//  1. AN UNKNOWN COST IS NOT ZERO, so it does not satisfy a numeric range. A
//     deal with no Sage location tagged has no GL balance to report, and a
//     "min $0" filter that swept those in would present them as the cheapest
//     deals in the book. They are therefore EXCLUDED by default whenever a
//     bound is set, and the caller opts them back in explicitly — the same rule
//     the report already applies when it counts `withoutCost` apart from the
//     total rather than folding nulls in as 0.
//
//  2. NO BOUNDS MEANS NO FILTER — not "everything with a cost". Clearing the
//     boxes has to give the book back, unknown-cost deals included, or the
//     filter becomes a trap you cannot fully undo.
// ============================================================

// A typed bound, in the shorthand people actually write in a box next to a
// dollar figure: "1,250,000" · "$1.25m" · "250k" · "2.5 M". Returns a number,
// or null for anything that is not a bound (empty, "-", "abc") — null being
// "no bound", which is why an unparseable value must not read as 0.
const MULTIPLIERS = { k: 1e3, m: 1e6, b: 1e9 };

export function parseCostBound(input) {
  if (input == null) return null;
  const raw = String(input).trim().toLowerCase();
  if (!raw) return null;

  // Strip currency formatting, keeping the sign: a negative bound is unusual
  // but a GL balance genuinely can be negative, so it is not rejected here.
  const cleaned = raw.replace(/[$,\s_]/g, '');
  const m = /^(-?(?:\d+\.?\d*|\.\d+))([kmb])?$/.exec(cleaned);
  if (!m) return null;

  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  return m[2] ? n * MULTIPLIERS[m[2]] : n;
}

// Is a cost range actually narrowing anything?
export const hasCostFilter = ({ min, max } = {}) => min != null || max != null;

// Does one deal pass the range? See the header for why a null cost is not 0.
export function matchesCost(deal, { min, max, includeUnknown = false } = {}) {
  if (min == null && max == null) return true;
  const cost = deal?.totalCost;
  if (cost == null) return includeUnknown;
  if (min != null && cost < min) return false;
  if (max != null && cost > max) return false;
  return true;
}

// What the range is doing, in words, so an active filter is never silent about
// having dropped rows. Returns null when nothing is filtered.
export function describeCostFilter({ min, max, includeUnknown = false } = {}, fmtMoney = String) {
  if (min == null && max == null) return null;
  const range = min != null && max != null ? `${fmtMoney(min)} – ${fmtMoney(max)}`
              : min != null               ? `at least ${fmtMoney(min)}`
              :                             `up to ${fmtMoney(max)}`;
  return `${range}${includeUnknown ? ', including deals with no recorded cost' : ''}`;
}

// A range typed backwards (min above max) matches nothing, which reads as a
// broken report rather than as a typo — so it is detected and said out loud.
export const isCostRangeInverted = ({ min, max } = {}) =>
  min != null && max != null && min > max;
